import {
  type DocumentEditor,
  type Selection,
  DocumentEditorContainerComponent,
} from "@syncfusion/ej2-react-documenteditor";
import { memo, useEffect, useRef } from "react";
import { wordProcessorHost } from "../api/config";

const handleTextSelected = (
  editor: DocumentEditor,
  setSelection: (s?: Selection) => void,
) => {
  setSelection(editor.selection.isEmpty ? undefined : editor.selection);
};

interface Props {
  documentBody: string;
  scrollToSelection?: Pick<Selection, "endPage" | "startOffset" | "endOffset">;
  setSelection?: (selection?: Selection) => void;
}

const DocumentViewer = memo(
  ({ documentBody, scrollToSelection, setSelection }: Props) => {
    const ref = useRef<DocumentEditorContainerComponent | null>(null);
    const eventHandlerRef = useRef<(() => void) | null>(null);

    useEffect(() => {
      if (ref.current) {
        const { documentEditor } = ref.current;

        // Load the document
        if (documentBody.length) documentEditor.open(documentBody);
        documentEditor.showRestrictEditingPane(false);
        documentEditor.isReadOnly = true;
        documentEditor.requestNavigate = () => null;

        if (scrollToSelection) {
          documentEditor.selection.select(
            scrollToSelection.startOffset,
            scrollToSelection.endOffset,
          );
        }
      }
    }, [documentBody, scrollToSelection]);

    useEffect(() => {
      if (ref.current && setSelection) {
        // Add event listeners for text selection
        const { documentEditor } = ref.current;
        eventHandlerRef.current = () =>
          handleTextSelected(documentEditor, setSelection);
        document.addEventListener("mouseup", eventHandlerRef.current);
        document.addEventListener("dblclick", eventHandlerRef.current);
      }

      return () => {
        if (eventHandlerRef.current) {
          document.removeEventListener("mouseup", eventHandlerRef.current);
          document.removeEventListener("dblclick", eventHandlerRef.current);
          eventHandlerRef.current = null;
        }
      };
    }, [setSelection]);

    return (
      <DocumentEditorContainerComponent
        id="document-viewer"
        ref={ref}
        height="100%"
        serviceUrl={`${wordProcessorHost}/`}
        readOnly={true}
        showPropertiesPane={false}
        enableToolbar={false}
      />
    );
  },
);

DocumentViewer.displayName = "DocumentViewer";

export default DocumentViewer;
